import { useEffect, useRef } from 'react';
import { CloseIcon } from '../../bombiix-icon';

export interface BombiixSidePanelProps {
  isOpen: boolean;
  title: React.ReactNode;
  onClose: () => void;
  onSave?: () => void;
  saveLabel?: React.ReactNode;
  saveDisabled?: boolean;
  saveLoading?: boolean;
  width?: number;
  anchorSelector?: string | null;
  headerLeft?: React.ReactNode;
  headerRight?: React.ReactNode;
  withBackdrop?: boolean;
  className?: string;
  closeAriaLabel?: string;
  closeDisabled?: boolean;
  testId?: string;
  saveTestId?: string;
  closeTestId?: string;
  children: React.ReactNode;
}

const DEFAULT_WIDTH = 504;
const DEFAULT_ANCHOR = '.page-title-filter';

/**
 * BombiixSidePanel — Bombiix master-design side sheet (Figma node 10845:1268723).
 * Right-anchored drawer with a 42px header bar (blue Save on the left when
 * `onSave` is provided — optionally preceded by `headerLeft` controls —
 * centered title, red 28×28 Close on the right).
 * Body content is passed as children. Styling lives in
 * `assets/scss/master-design/_side-panel.scss` — no per-component CSS.
 * `testId` stamps the root and derives `${testId}-save` / `${testId}-close`
 * on the header buttons unless `saveTestId` / `closeTestId` override them.
 */
function BombiixSidePanel({
  isOpen,
  title,
  onClose,
  onSave,
  saveLabel,
  saveDisabled,
  saveLoading,
  width = DEFAULT_WIDTH,
  anchorSelector = DEFAULT_ANCHOR,
  headerLeft,
  headerRight,
  withBackdrop,
  className,
  closeAriaLabel = 'Close',
  closeDisabled,
  testId,
  saveTestId,
  closeTestId,
  children,
}: BombiixSidePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !anchorSelector) return undefined;

    const updatePosition = () => {
      const anchor = document.querySelector(anchorSelector);
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const panel = panelRef.current;
      const backdrop = backdropRef.current;
      if (panel) {
        panel.style.top = `${rect.bottom}px`;
        panel.style.height = `calc(100vh - ${rect.bottom}px)`;
      }
      if (backdrop) {
        backdrop.style.top = `${rect.bottom}px`;
        backdrop.style.height = `calc(100vh - ${rect.bottom}px)`;
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, anchorSelector]);

  if (!isOpen) return null;

  const rootClass = ['bombiix-side-panel', 'bombiix-side-panel--open', className].filter(Boolean).join(' ');

  return (
    <>
      {withBackdrop ? <div ref={backdropRef} className="bombiix-side-panel__backdrop" onClick={onClose} role="presentation" /> : null}
      <aside ref={panelRef} className={rootClass} style={{ width: `${width}px`, maxWidth: `${width}px` }} data-testid={testId}>
        <div className="bombiix-side-panel__bar">
          {headerLeft || onSave ? (
            <div className="bombiix-side-panel__header-left">
              {headerLeft}
              {onSave ? (
                <button
                  type="button"
                  className="bombiix-btn btn-blue bombiix-side-panel__save"
                  onClick={onSave}
                  disabled={saveDisabled || saveLoading}
                  data-testid={saveTestId ?? (testId ? `${testId}-save` : undefined)}
                >
                  {saveLabel ?? 'Save'}
                </button>
              ) : null}
            </div>
          ) : (
            <span className="bombiix-side-panel__spacer" aria-hidden="true" />
          )}
          <div className="bombiix-side-panel__title">{title}</div>
          <div className="bombiix-side-panel__header-right">
            {headerRight}
            <button
              type="button"
              className="bombiix-side-panel__close"
              onClick={onClose}
              disabled={closeDisabled}
              aria-label={closeAriaLabel}
              data-testid={closeTestId ?? (testId ? `${testId}-close` : undefined)}
            >
              <CloseIcon size={28} />
            </button>
          </div>
        </div>
        <div className="bombiix-side-panel__body">{children}</div>
      </aside>
    </>
  );
}

export default BombiixSidePanel;
